import { DestroyRef, Directive, ElementRef, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

/** The root custom property fixed overlays read for the content area's real top edge. */
export const APP_BODY_TOP_PROPERTY = '--app-body-top';

/**
 * THE CONTENT AREA'S TOP — mounted on the shell's body (the row under the topbar), it publishes where that row
 * starts as `--app-body-top` on the document root.
 *
 * Fixed overlays that sit below the topbar (the slide panels and their backdrop) are mounted outside the layout,
 * so they cannot inherit its geometry; and the stack above the topbar has no fixed height — the demo, subscription,
 * trial and temporal banners and the connectivity bar come and go, and a banner wraps on a narrow window. A
 * constant offset therefore lands the overlay over the topbar whenever a banner shows.
 *
 * The body fills the rest of a full-height column, so its own height changes whenever anything above it changes
 * height or the window resizes; one observer on it re-measures on every such change, on no timer. The property is
 * removed with the body, so a reader's fallback applies wherever the shell is not mounted.
 */
@Directive({
  selector: '[appBodyOffset]',
  standalone: true,
})
export class AppBodyOffsetDirective {
  constructor() {
    const host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    const root = inject(DOCUMENT).documentElement;
    const measure = () => root.style.setProperty(APP_BODY_TOP_PROPERTY, `${host.getBoundingClientRect().top}px`);

    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    resize?.observe(host);

    inject(DestroyRef).onDestroy(() => {
      resize?.disconnect();
      root.style.removeProperty(APP_BODY_TOP_PROPERTY);
    });
  }
}
